/**
 * Collection "Load more" (templates/collection.liquid).
 * The [data-load-more] link points to the next page. On click we fetch that page, append its
 * #product-grid cards to the current grid and point the link at the following page (or remove it).
 * Sold out cards are already skipped in Liquid, so a fetched page can be empty: we then keep going
 * until something is added or there are no more pages.
 * Click handling is delegated on document, so it keeps working after filters re-render the grid.
 */

const MAX_EMPTY_PAGES = 10;

/** @param {Document} doc */
const nextUrlFrom = (doc) => doc.querySelector('[data-load-more]')?.getAttribute('href') || null;

/**
 * Swap only the label text; the spinner next to it shows while aria-busy is set.
 * @param {HTMLAnchorElement} link
 * @param {string | undefined} text
 */
function setLabel(link, text) {
  const label = link.querySelector('[data-load-more-label]');
  if (label && text) label.textContent = text;
}

/** @param {HTMLAnchorElement} link */
async function loadMore(link) {
  const grid = document.getElementById('product-grid');
  const wrap = link.closest('[data-load-more-wrap]');
  const error = wrap?.querySelector('[data-load-more-error]');
  if (!grid || link.getAttribute('aria-busy') === 'true') return;

  link.setAttribute('aria-busy', 'true');
  setLabel(link, link.dataset.loadingLabel);
  if (error) error.hidden = true;

  /** @type {string | null} */
  let url = link.getAttribute('href');
  let added = 0;
  let tries = 0;

  try {
    while (url && added === 0 && tries < MAX_EMPTY_PAGES) {
      tries += 1;
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = doc.querySelectorAll('#product-grid > li');
      for (const item of items) {
        grid.append(document.importNode(item, true));
        added += 1;
      }
      url = nextUrlFrom(doc);
    }

    if (url) {
      link.setAttribute('href', url);
      link.removeAttribute('aria-busy');
      setLabel(link, link.dataset.label);
    } else {
      wrap?.remove();
    }
  } catch {
    link.removeAttribute('aria-busy');
    setLabel(link, link.dataset.label);
    if (error) error.hidden = false;
  }
}

/** If the visible page has no cards (all sold out) but more pages exist, load them right away. */
function fillIfEmpty() {
  const grid = document.getElementById('product-grid');
  const link = document.querySelector('[data-load-more]');
  if (grid && link instanceof HTMLAnchorElement && grid.children.length === 0) loadMore(link);
}
fillIfEmpty();
// Filters re-render the grid partial; check again after each update.
new MutationObserver(() => fillIfEmpty()).observe(document.body, { childList: true, subtree: true });

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-load-more]') : null;
  if (!(target instanceof HTMLAnchorElement)) return;
  // Let modified clicks (new tab etc.) behave like a normal link.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
  event.preventDefault();
  loadMore(target);
});
